/* eslint-disable no-unused-vars */
import React, { useState, useCallback } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  Image,
  Alert,
  FlatList,
  Modal,
  ScrollView,
  StatusBar,
  ActivityIndicator,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  MaterialIcons,
} from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  companyProfile: `${API_BASE_URL}/api/data/company_profiles`,
  vendorCategories: `${API_BASE_URL}/api/data/vendor_categories`,
  vendorSpecialties: `${API_BASE_URL}/api/data/vendor_specialties`,
  vendors: `${API_BASE_URL}/api/data/vendors`, 
};

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
  },
});

api.interceptors.request.use(
  async (config) => {
    try {
      const token = await AsyncStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        config.params = { ...config.params, company_id: parseInt(companyId) };
      }
    } catch (error) {
      console.error('Error in request interceptor:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const VendorCategoriesScreen = ({ navigation }) => {
  const [activeTab, setActiveTab] = useState('Categories');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [companyLogo, setCompanyLogo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [specialties, setSpecialties] = useState([]);

  useFocusEffect(
    useCallback(() => {
      loadCompanyLogo();
      fetchData();
    }, [])
  );

  const loadCompanyLogo = async () => {
    try {
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        const profile = JSON.parse(cachedProfile);
        setCompanyLogo(profile.logo || null);
      }
      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        const response = await api.get(API_ENDPOINTS.companyProfile, {
          params: { company_id: companyId },
        });
        if (response.data?.success && response.data.data?.length > 0) {
          const latestProfile = response.data.data[0];
          setCompanyLogo(latestProfile.logo || null);
          await AsyncStorage.setItem('company_profile', JSON.stringify(latestProfile));
        }
      }
    } catch (error) {
      console.warn('Error loading company logo:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const companyId = await AsyncStorage.getItem('company_id');
      const [categoriesRes, specialtiesRes] = await Promise.allSettled([
        api.get(API_ENDPOINTS.vendorCategories, { params: { company_id: companyId } }),
        api.get(API_ENDPOINTS.vendorSpecialties, { params: { company_id: companyId } }),
      ]);

      if (categoriesRes.status === 'fulfilled' && categoriesRes.value.data?.success) {
        setCategories(categoriesRes.value.data.data);
      } else {
        // fallback mock
        setCategories([
          { id: 'cat1', name: 'Concrete & Foundation', description: 'Concrete work, foundation laying', vendorCount: 5, activeProjects: 8, averageRating: 4.5, specialties: ['Concrete Work', 'Foundation'], color: '#0e4e96', icon: 'construct' },
          { id: 'cat2', name: 'Electrical Systems', description: 'Electrical installation', vendorCount: 4, activeProjects: 6, averageRating: 4.3, specialties: ['Electrical', 'Lighting'], color: '#fdb913', icon: 'flash' },
          { id: 'cat3', name: 'Plumbing & Water', description: 'Plumbing installation, drainage, and water systems', vendorCount: 3, activeProjects: 4, averageRating: 4.4, specialties: ['Plumbing', 'Drainage', 'Water Systems'], color: '#10B981', icon: 'water' },
          { id: 'cat4', name: 'Roofing & Exterior', description: 'Roofing, waterproofing, and exterior work', vendorCount: 3, activeProjects: 3, averageRating: 4.2, specialties: ['Roofing', 'Waterproofing', 'Insulation'], color: '#EF4444', icon: 'home' },
          { id: 'cat5', name: 'Interior Finishing', description: 'Drywall, plastering, painting, and interior work', vendorCount: 4, activeProjects: 5, averageRating: 4.1, specialties: ['Drywall', 'Plastering', 'Painting', 'Interior Finishing'], color: '#64748B', icon: 'brush' },
          { id: 'cat6', name: 'HVAC Systems', description: 'Heating, ventilation, and air conditioning', vendorCount: 2, activeProjects: 2, averageRating: 4.0, specialties: ['HVAC', 'Heating', 'Ventilation', 'Air Conditioning'], color: '#0e4e96', icon: 'thermometer' },
        ]);
      }

      if (specialtiesRes.status === 'fulfilled' && specialtiesRes.value.data?.success) {
        setSpecialties(specialtiesRes.value.data.data);
      } else {
        setSpecialties([
          { id: 's1', name: 'Concrete Work', category: 'Concrete & Foundation', vendors: 5 },
          { id: 's2', name: 'Foundation', category: 'Concrete & Foundation', vendors: 4 },
          { id: 's3', name: 'Structural Concrete', category: 'Concrete & Foundation', vendors: 3 },
          { id: 's4', name: 'Electrical', category: 'Electrical Systems', vendors: 4 },
          { id: 's5', name: 'Lighting', category: 'Electrical Systems', vendors: 3 },
          { id: 's6', name: 'Plumbing', category: 'Plumbing & Water', vendors: 3 },
          { id: 's7', name: 'Drainage', category: 'Plumbing & Water', vendors: 2 },
          { id: 's8', name: 'Roofing', category: 'Roofing & Exterior', vendors: 3 },
          { id: 's9', name: 'Waterproofing', category: 'Roofing & Exterior', vendors: 2 },
          { id: 's10', name: 'Drywall', category: 'Interior Finishing', vendors: 4 },
          { id: 's11', name: 'Painting', category: 'Interior Finishing', vendors: 3 },
          { id: 's12', name: 'HVAC', category: 'HVAC Systems', vendors: 2 },
        ]);
      }
    } catch (error) {
      console.error('Error fetching vendor data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCategories = categories.filter(cat =>
    String(cat.name ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    String(cat.description ?? '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredSpecialties = specialties.filter(spec =>
    String(spec.name ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    String(spec.category ?? '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      Alert.alert('Error', 'Category name is required');
      return;
    }
    try {
      const companyId = await AsyncStorage.getItem('company_id');
      const payload = {
        company_id: parseInt(companyId),
        name: newCategoryName,
        description: newCategoryDescription,
      };
      const response = await api.post(API_ENDPOINTS.vendorCategories, payload);
      setCategories(prev => [...prev, response.data.data]);
      setNewCategoryName('');
      setNewCategoryDescription('');
      setShowAddModal(false);
      Alert.alert('Success', 'Category added');
    } catch (error) {
      Alert.alert('Error', 'Could not add category');
    }
  };

  const handleUpdateCategory = async () => {
    if (!newCategoryName.trim()) {
      Alert.alert('Error', 'Category name is required');
      return;
    }
    try {
      const payload = { name: newCategoryName, description: newCategoryDescription };
      await api.put(`${API_ENDPOINTS.vendorCategories}/${editingCategory.id}`, payload);
      setCategories(prev => prev.map(c => c.id === editingCategory.id ? { ...c, ...payload } : c));
      setEditingCategory(null);
      setNewCategoryName('');
      setNewCategoryDescription('');
      setShowAddModal(false);
      Alert.alert('Success', 'Category updated');
    } catch (error) {
      Alert.alert('Error', 'Could not update category');
    }
  };

  const handleDeleteCategory = (id) => {
    Alert.alert('Delete Category', 'Are you sure?', [
      { text: 'Cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: async () => {
          try {
            await api.delete(`${API_ENDPOINTS.vendorCategories}/${id}`);
            setCategories(prev => prev.filter(c => c.id !== id));
            Alert.alert('Success', 'Category deleted');
          } catch (error) {
            Alert.alert('Error', 'Could not delete category');
          }
        },
      },
    ]);
  };

  const handleGoBack = () => {
    if (navigation.canGoBack()) navigation.goBack();
  };

  const renderCategoryCard = ({ item }) => (
    <TouchableOpacity style={styles.categoryCard} onPress={() => navigation.navigate('CategoryDetail', { category: item })}>
      <View style={styles.categoryHeader}>
        <View style={[styles.categoryIcon, { backgroundColor: item.color }]}>
          <Ionicons name={item.icon} size={24} color="#fff" />
        </View>
        <View style={styles.categoryInfo}>
          <Text style={styles.categoryName}>{item.name}</Text>
          <Text style={styles.categoryDescription}>{item.description}</Text>
        </View>
        <TouchableOpacity style={styles.categoryMenuButton} onPress={() => {
          Alert.alert('Options', '', [
            { text: 'Edit', onPress: () => { setEditingCategory(item); setNewCategoryName(item.name); setNewCategoryDescription(item.description); setShowAddModal(true); } },
            { text: 'Delete', style: 'destructive', onPress: () => handleDeleteCategory(item.id) },
            { text: 'Cancel', style: 'cancel' },
          ]);
        }}>
          <Ionicons name="ellipsis-vertical" size={20} color="#64748B" />
        </TouchableOpacity>
      </View>
      <View style={styles.categoryStats}>
        <View style={styles.statItem}><Text style={styles.statValue}>{item.vendorCount}</Text><Text style={styles.statLabel}>Vendors</Text></View>
        <View style={styles.statItem}><Text style={styles.statValue}>{item.activeProjects}</Text><Text style={styles.statLabel}>Projects</Text></View>
        <View style={styles.statItem}>
          <View style={styles.ratingContainer}>
            <Ionicons name="star" size={14} color="#fdb913" />
            <Text style={styles.statValue}>{Number(item.averageRating).toFixed(1)}</Text>
          </View>
          <Text style={styles.statLabel}>Avg Rating</Text>
        </View>
      </View>
      <View style={styles.specialtiesPreview}>
        <Text style={styles.specialtiesLabel}>Specialties:</Text>
        <View style={styles.specialtiesTags}>
          {item.specialties.slice(0, 3).map((spec, idx) => (
            <View key={idx} style={styles.specialtyTag}><Text style={styles.specialtyTagText}>{spec}</Text></View>
          ))}
          {item.specialties.length > 3 && <View style={styles.specialtyTag}><Text style={styles.specialtyTagText}>+{item.specialties.length - 3}</Text></View>}
        </View>
      </View>
    </TouchableOpacity>
  );

  const renderSpecialtyCard = ({ item }) => (
    <View style={styles.specialtyCard}>
      <View style={styles.specialtyHeader}>
        <Text style={styles.specialtyName}>{item.name}</Text>
        <TouchableOpacity style={styles.specialtyMenuButton}><Ionicons name="ellipsis-vertical" size={16} color="#64748B" /></TouchableOpacity>
      </View>
      <Text style={styles.specialtyCategory}>Category: {item.category}</Text>
      <View style={styles.specialtyStats}>
        <View style={styles.specialtyStatItem}><Ionicons name="people" size={16} color="#0e4e96" /><Text style={styles.specialtyStatText}>{item.vendors} vendors</Text></View>
      </View>
    </View>
  );

  const renderAddModal = () => (
    <Modal visible={showAddModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => { setShowAddModal(false); setEditingCategory(null); setNewCategoryName(''); setNewCategoryDescription(''); }}>
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <TouchableOpacity onPress={() => { setShowAddModal(false); setEditingCategory(null); setNewCategoryName(''); setNewCategoryDescription(''); }}><Text style={styles.modalCancelText}>Cancel</Text></TouchableOpacity>
          <Text style={styles.modalTitle}>{editingCategory ? 'Edit Category' : 'Add Category'}</Text>
          <TouchableOpacity onPress={editingCategory ? handleUpdateCategory : handleAddCategory}><Text style={styles.modalSaveText}>{editingCategory ? 'Update' : 'Save'}</Text></TouchableOpacity>
        </View>
        <ScrollView style={styles.modalContent}>
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Category Name *</Text>
            <TextInput style={styles.textInput} value={newCategoryName} onChangeText={setNewCategoryName} placeholder="Enter category name" placeholderTextColor="#94A3B8" />
          </View>
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Description</Text>
            <TextInput style={[styles.textInput, styles.textArea]} value={newCategoryDescription} onChangeText={setNewCategoryDescription} placeholder="Enter description" placeholderTextColor="#94A3B8" multiline numberOfLines={4} textAlignVertical="top" />
          </View>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  const renderHeader = () => (
    <View style={styles.header}>
      <TouchableOpacity style={styles.backButton} onPress={handleGoBack}>
        <Ionicons name="chevron-back" size={24} color="#fff" />
      </TouchableOpacity>
      <View style={styles.headerCenter}>
        {companyLogo ? <Image source={{ uri: companyLogo || undefined }} style={styles.headerLogo} /> : <MaterialCommunityIcons name="office-building" size={30} color="#fff" />}
        <Text style={styles.headerTitle}>Vendor Categories</Text>
      </View>
      <TouchableOpacity style={styles.headerButton} onPress={() => setShowAddModal(true)}>
        <Ionicons name="add" size={24} color="#fff" />
      </TouchableOpacity>
    </View>
  );

  if (loading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
        {renderHeader()}
        <View style={styles.loadingContainer}><ActivityIndicator size={36} color="#0e4e96" /><Text style={styles.loadingText}>Loading categories...</Text></View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
      {renderHeader()}
      <View style={styles.searchContainer}>
        <View style={styles.searchBar}>
          <Ionicons name="search" size={20} color="#64748B" />
          <TextInput style={styles.searchInput} placeholder="Search categories or specialties..." value={searchQuery} onChangeText={setSearchQuery} placeholderTextColor="#94A3B8" />
          {searchQuery ? <TouchableOpacity onPress={() => setSearchQuery('')}><Ionicons name="close-circle" size={20} color="#64748B" /></TouchableOpacity> : null}
        </View>
      </View>
      <View style={styles.tabContainer}>
        {['Categories', 'Specialties'].map(tab => (
          <TouchableOpacity key={tab} style={[styles.tabButton, activeTab === tab && styles.activeTabButton]} onPress={() => setActiveTab(tab)}>
            <Text style={[styles.tabText, activeTab === tab && styles.activeTabText]}>{tab}</Text>
          </TouchableOpacity>
        ))}
      </View>
      {activeTab === 'Categories' ? (
        <FlatList
          data={filteredCategories}
          renderItem={renderCategoryCard}
          keyExtractor={item => item.id.toString()}
          contentContainerStyle={styles.listContainer}
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <MaterialIcons name="category" size={48} color="#CBD5E1" />
              <Text style={styles.emptyTitle}>No categories found</Text>
              <Text style={styles.emptyText}>Try adjusting your search or add a new category</Text>
            </View>
          }
        />
      ) : (
        <FlatList
          data={filteredSpecialties}
          renderItem={renderSpecialtyCard}
          keyExtractor={item => item.id.toString()}
          contentContainerStyle={styles.listContainer}
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <MaterialIcons name="work" size={48} color="#CBD5E1" />
              <Text style={styles.emptyTitle}>No specialties found</Text>
              <Text style={styles.emptyText}>Try adjusting your search criteria</Text>
            </View>
          }
        />
      )}
      {renderAddModal()}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  // ... (same as before, unchanged)
  safeArea: { flex: 1, backgroundColor: '#4A90E2' },
  header: {
    backgroundColor: '#4A90E2',
    paddingVertical: 15,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: { padding: 5 },
  headerCenter: { flexDirection: 'row', alignItems: 'center' },
  headerLogo: { width: 30, height: 30, borderRadius: 15, resizeMode: 'cover' },
  headerTitle: { color: '#fff', fontSize: 20, fontWeight: 'bold', marginLeft: 10 },
  headerButton: { padding: 5 },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#f8fafc' },
  loadingText: { marginTop: 10, fontSize: 16, color: '#64748B' },
  searchContainer: { paddingHorizontal: 15, paddingVertical: 10, backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#e2e8f0' },
  searchBar: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#f1f5f9', borderRadius: 8, paddingHorizontal: 12, height: 40 },
  searchInput: { flex: 1, fontSize: 16, color: '#0f172a', marginLeft: 8 },
  tabContainer: { backgroundColor: '#fff', flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#e2e8f0' },
  tabButton: { flex: 1, paddingVertical: 15, alignItems: 'center', borderBottomWidth: 2, borderBottomColor: 'transparent' },
  activeTabButton: { borderBottomColor: '#0e4e96' },
  tabText: { fontSize: 16, color: '#64748B', fontWeight: '500' },
  activeTabText: { color: '#0e4e96', fontWeight: '600' },
  listContainer: { padding: 12, backgroundColor: '#f8fafc' },
  categoryCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 15,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 10,
    elevation: 2,
  },
  categoryHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 15 },
  categoryIcon: { width: 50, height: 50, borderRadius: 25, justifyContent: 'center', alignItems: 'center', marginRight: 15 },
  categoryInfo: { flex: 1 },
  categoryName: { fontSize: 18, fontWeight: '600', color: '#0f172a', marginBottom: 3 },
  categoryDescription: { fontSize: 14, color: '#64748B', lineHeight: 18 },
  categoryMenuButton: { padding: 5 },
  categoryStats: { flexDirection: 'row', justifyContent: 'space-around', marginBottom: 15, backgroundColor: '#f1f5f9', borderRadius: 8, paddingVertical: 12, borderWidth: 1, borderColor: '#e2e8f0' },
  statItem: { alignItems: 'center' },
  statValue: { fontSize: 18, fontWeight: 'bold', color: '#0e4e96', marginBottom: 2 },
  statLabel: { fontSize: 12, color: '#64748B' },
  ratingContainer: { flexDirection: 'row', alignItems: 'center' },
  specialtiesPreview: { borderTopWidth: 1, borderTopColor: '#e2e8f0', paddingTop: 12 },
  specialtiesLabel: { fontSize: 14, fontWeight: '600', color: '#0f172a', marginBottom: 8 },
  specialtiesTags: { flexDirection: 'row', flexWrap: 'wrap' },
  specialtyTag: { backgroundColor: '#f1f5f9', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 4, marginRight: 6, marginBottom: 6, borderWidth: 1, borderColor: '#e2e8f0' },
  specialtyTagText: { fontSize: 10, color: '#0e4e96', fontWeight: '500' },
  specialtyCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 15,
    marginBottom: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 10,
    elevation: 2,
  },
  specialtyHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  specialtyName: { fontSize: 16, fontWeight: '600', color: '#0f172a' },
  specialtyCategory: { fontSize: 12, color: '#64748B', marginBottom: 10 },
  specialtyStats: { flexDirection: 'row', alignItems: 'center' },
  specialtyStatItem: { flexDirection: 'row', alignItems: 'center' },
  specialtyStatText: { fontSize: 12, color: '#64748B', marginLeft: 5 },
  emptyContainer: { alignItems: 'center', justifyContent: 'center', padding: 40, backgroundColor: '#f8fafc' },
  emptyTitle: { fontSize: 18, fontWeight: '600', color: '#0f172a', marginTop: 16, marginBottom: 8 },
  emptyText: { fontSize: 14, color: '#64748B', textAlign: 'center' },
  modalContainer: { flex: 1, backgroundColor: '#fff' },
  modalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 15, borderBottomWidth: 1, borderBottomColor: '#e2e8f0' },
  modalCancelText: { fontSize: 16, color: '#64748B' },
  modalTitle: { fontSize: 18, fontWeight: '600', color: '#0f172a' },
  modalSaveText: { fontSize: 16, color: '#0e4e96', fontWeight: '600' },
  modalContent: { flex: 1, padding: 15 },
  inputGroup: { marginBottom: 20 },
  inputLabel: { fontSize: 14, fontWeight: '600', color: '#0f172a', marginBottom: 5 },
  textInput: {
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: '#0f172a',
    backgroundColor: '#f1f5f9',
  },
  textArea: { minHeight: 80, textAlignVertical: 'top' },
});

export default VendorCategoriesScreen;